import { format } from "date-fns";

import { calculateAge } from "@utils/datetime";

/**
 * DOB formats used in this module:
 * - **Display / input:** `MM/dd/yyyy` (e.g. `06/15/1990`) — enrollment fields, survey inputs
 * - **API date:** `YYYY-MM-DD` (e.g. `1990-06-15`) — patient save, user model sync
 * - **Stored ISO:** ISO date/datetime string or `Date` — some survey answers (e.g. insurance checker)
 */

/** Max length of a complete display DOB string (`MM/dd/yyyy`). */
export const DOB_INPUT_MAX_LENGTH = 10;

export const DOB_ERROR_KEYS = {
  invalidFormat: "dateOfBirth:errorMessages:invalidFormat",
  invalid: "dateOfBirth:errorMessages:invalid",
  after1900: "dateOfBirth:errorMessages:after1900",
  future: "dateOfBirth:errorMessages:future",
} as const;

const DISPLAY_DOB_PATTERN =
  /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;

const API_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Validates calendar date components and returns a local `Date` at noon.
 *
 * @param year - Four-digit calendar year.
 * @param month - Calendar month (1–12).
 * @param day - Day of month (1–31).
 * @returns Local `Date` at 12:00, or `null` if the date is invalid (e.g. Feb 30).
 */
function localNoonFromYmd(
  year: number,
  month: number,
  day: number
): Date | null {
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return new Date(year, month - 1, day, 12, 0, 0);
}

/**
 * Formats raw keystrokes into a display DOB string as the user types.
 *
 * @param value - Raw input (digits and/or slashes).
 * @returns Partial or complete `MM/dd/yyyy` (may be incomplete while typing).
 */
export function formatDobInput(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 2) {
    return digits;
  }
  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
}

/**
 * Parses a complete display DOB into a local calendar date at noon.
 *
 * @param dateStr - Complete `MM/dd/yyyy` string.
 * @returns Local `Date` at 12:00, or `null` if the string is invalid.
 */
export function parseDobInputToLocalNoon(dateStr: string): Date | null {
  if (!DISPLAY_DOB_PATTERN.test(dateStr)) {
    return null;
  }

  const [month, day, year] = dateStr.split("/").map(Number);
  return localNoonFromYmd(year, month, day);
}

/**
 * Normalizes a stored DOB into display format for text inputs.
 *
 * Accepts `MM/dd/yyyy`, ISO date/datetime strings, or `Date`.
 * Does not validate the calendar date (use {@link resolveValidDisplayDob}).
 *
 * @param value - Stored DOB (`MM/dd/yyyy`, ISO string, `Date`, or empty).
 * @returns `MM/dd/yyyy`, or `""` if empty, partial, or unparseable.
 */
export function formatDobToDisplay(value: unknown): string {
  let normalizedValue = value;
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "";
    }
    normalizedValue = value[0];
  }

  if (!normalizedValue) {
    return "";
  }

  if (normalizedValue instanceof Date) {
    return formatIsoDobForDisplay(normalizedValue.toISOString());
  }

  if (typeof normalizedValue !== "string") {
    return "";
  }

  if (DISPLAY_DOB_PATTERN.test(normalizedValue)) {
    return normalizedValue;
  }

  if (
    normalizedValue.includes("/") &&
    normalizedValue.length < DOB_INPUT_MAX_LENGTH
  ) {
    return "";
  }

  return formatIsoDobForDisplay(normalizedValue);
}

/**
 * Converts an ISO date/datetime string to display format.
 *
 * @param storedDate - ISO string (e.g. `1990-06-15` or `1990-06-15T12:00:00.000Z`).
 * @returns `MM/dd/yyyy`, or `""` if missing or unparseable.
 */
export function formatIsoDobForDisplay(storedDate?: string | null): string {
  if (!storedDate) {
    return "";
  }

  if (API_DATE_PATTERN.test(storedDate)) {
    const [year, month, day] = storedDate.split("-").map(Number);
    const date = localNoonFromYmd(year, month, day);
    if (!date) {
      return "";
    }
    return format(date, "MM/dd/yyyy");
  }

  const date = new Date(storedDate);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return format(date, "MM/dd/yyyy");
}

/**
 * Coerces survey-stored DOB values to an ISO datetime string without validation.
 *
 * @param value - `MM/dd/yyyy`, ISO string, `Date`, or empty.
 * @returns ISO datetime string (passthrough for strings), or `undefined` if empty.
 */
export function surveyDobValueToIsoString(
  value: string | Date | undefined | null
): string | undefined {
  if (value == null) {
    return undefined;
  }
  if (typeof value === "string") {
    return value;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return undefined;
}

/** @returns Whether `date` is on or before the end of today (local time). */
export function isLocalDateOnOrBeforeEndOfToday(date: Date): boolean {
  const today = new Date();
  const endOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    23,
    59,
    59,
    999
  );
  return date.getTime() <= endOfToday.getTime();
}

/**
 * Validates a display DOB and parses it to a local date.
 *
 * @param displayValue - `MM/dd/yyyy` (complete or partial while typing).
 * @param options - Validation options.
 * @param options.showIncompleteError - When `true`, partial input (e.g. `06/15`) returns
 *   `invalidFormat`. When `false` (default), partial input returns no error — use while typing.
 * @returns `parsedDate` (local noon) and an i18n `errorKey`, or both null when empty.
 */
export function validateAndParseDob(
  displayValue: string,
  options: { showIncompleteError?: boolean } = {}
): {
  parsedDate: Date | null;
  errorKey: (typeof DOB_ERROR_KEYS)[keyof typeof DOB_ERROR_KEYS] | null;
} {
  const { showIncompleteError = false } = options;

  if (!displayValue) {
    return { parsedDate: null, errorKey: null };
  }

  if (displayValue.length < DOB_INPUT_MAX_LENGTH) {
    if (showIncompleteError && displayValue.length > 0) {
      return {
        parsedDate: null,
        errorKey: DOB_ERROR_KEYS.invalidFormat,
      };
    }

    return { parsedDate: null, errorKey: null };
  }

  const parsedDate = parseDobInputToLocalNoon(displayValue);
  if (!parsedDate) {
    return {
      parsedDate: null,
      errorKey: DOB_ERROR_KEYS.invalid,
    };
  }

  if (parsedDate.getFullYear() < 1900) {
    return {
      parsedDate: null,
      errorKey: DOB_ERROR_KEYS.after1900,
    };
  }

  if (!isLocalDateOnOrBeforeEndOfToday(parsedDate)) {
    return {
      parsedDate: null,
      errorKey: DOB_ERROR_KEYS.future,
    };
  }

  return { parsedDate, errorKey: null };
}

/**
 * Normalizes and validates a stored DOB in one pass.
 *
 * @param value - Stored DOB (`MM/dd/yyyy`, ISO string, `Date`, or empty).
 * @returns `{ display: MM/dd/yyyy, parsedDate: Date }`, or `null` if invalid.
 */
function formatAndValidateDob(
  value: unknown
): { displayValue: string; parsedDate: Date } | null {
  const displayValue = formatDobToDisplay(value);
  if (!displayValue) {
    return null;
  }

  const { parsedDate } = validateAndParseDob(displayValue);
  if (!parsedDate) {
    return null;
  }

  return { displayValue, parsedDate };
}

/**
 * Returns a validated display DOB, or empty string.
 *
 * @param value - Stored DOB (`MM/dd/yyyy`, ISO string, `Date`, or empty).
 * @returns Valid `MM/dd/yyyy`, or `""` if missing, partial, or invalid.
 */
export function resolveValidDisplayDob(value: unknown): string {
  return formatAndValidateDob(value)?.displayValue ?? "";
}

/**
 * Normalizes a stored DOB to API date format.
 *
 * @param value - Stored DOB (`MM/dd/yyyy`, ISO string, `Date`, or empty).
 * @returns `YYYY-MM-DD` for APIs, or `null` if missing, partial, or invalid.
 */
export function formatDobToIsoDateString(value: unknown): string | null {
  const parsed = formatAndValidateDob(value);
  if (!parsed) {
    return null;
  }

  return format(parsed.parsedDate, "yyyy-MM-dd");
}

/**
 * @param date - Parsed DOB (local calendar date).
 * @param minAge - Minimum allowed age (inclusive).
 * @param maxAge - Maximum allowed age (inclusive).
 * @returns Whether age from `date` falls within `[minAge, maxAge]` (inclusive).
 */
export function isDobAgeBetweenInclusive(
  date: Date,
  minAge: number,
  maxAge: number
): boolean {
  const age = calculateAge(date);
  return age >= minAge && age <= maxAge;
}
